import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { AlertCircle, Eye, EyeOff, Loader2 } from "lucide-react";
import { useState } from "react";
import { AuthenticatedRedirect } from "../components/auth/AuthenticatedRedirect";
import { DiscordIcon } from "../components/icons/BrandIcons";
import { GoogleIcon } from "../components/icons/GoogleIcon";
import { RivaledFullLogo } from "../components/logos/RivaledFullLogo";
import { Button, Input } from "../components/ui";
import { signIn } from "../lib/auth-client";
import { getAuthCallbackUrl } from "../lib/authCallbackUrl";

export const Route = createFileRoute("/sign-in")({
  component: SignInPage,
});

function SignInPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const { error: signInError } = await signIn.email({
        email,
        password,
        callbackURL: "/app",
      });

      if (signInError) {
        setError(signInError.message ?? "Invalid email or password");
        setIsLoading(false);
        return;
      }

      void navigate({ to: "/app" });
    } catch {
      setError("Unable to reach the server. Check your connection and try again.");
      setIsLoading(false);
    }
  };

  const handleSocialSignIn = (provider: "discord" | "google") => {
    void signIn.social({
      provider,
      callbackURL: getAuthCallbackUrl("/app"),
    });
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-surface-950 overflow-hidden">
      <AuthenticatedRedirect />
      {/* Background effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_0%,rgba(38,217,181,0.06),transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_100%,rgba(38,217,181,0.03),transparent_50%)]" />
        <div className="absolute inset-0 noise-overlay" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="relative w-full max-w-md mx-4"
      >
        {/* Logo */}
        <div className="flex justify-center mb-10">
          <Link to="/">
            <RivaledFullLogo className="h-10 opacity-80 hover:opacity-100 transition-opacity" />
          </Link>
        </div>

        {/* Card */}
        <div className="relative rounded-2xl bg-surface-900/80 border border-surface-700/50 backdrop-blur-sm overflow-hidden">
          {/* Top accent line */}
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-500/40 to-transparent" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(38,217,181,0.04)_0%,_transparent_60%)] pointer-events-none" />

          <div className="relative px-8 py-10">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-display font-bold tracking-tight text-white uppercase">
                Welcome Back
              </h1>
              <p className="text-sm text-surface-400 mt-2">Sign in to enter the arena</p>
            </div>

            {/* Social providers */}
            <div className="flex gap-3 mb-6">
              <button
                type="button"
                onClick={() => handleSocialSignIn("discord")}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-surface-800 border border-surface-700 text-surface-200 hover:bg-surface-700 hover:border-surface-600 transition-all duration-200 text-sm font-medium"
              >
                <DiscordIcon className="size-4 text-[#5865F2]" />
                Discord
              </button>
              <button
                type="button"
                onClick={() => handleSocialSignIn("google")}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-surface-800 border border-surface-700 text-surface-200 hover:bg-surface-700 hover:border-surface-600 transition-all duration-200 text-sm font-medium"
              >
                <GoogleIcon className="size-4" />
                Google
              </button>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-4 mb-6">
              <div className="flex-1 h-px bg-surface-700/50" />
              <span className="text-xs text-surface-500 uppercase tracking-widest font-mono">
                or
              </span>
              <div className="flex-1 h-px bg-surface-700/50" />
            </div>

            {/* Email/password form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                label="Email"
                id="email"
                name="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                placeholder="you@example.com"
              />

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label htmlFor="password" className="text-sm font-medium text-content-secondary">
                    Password
                  </label>
                  <Link
                    to="/forgot-password"
                    className="text-xs text-brand-400 hover:text-brand-300 transition-colors"
                  >
                    Forgot password?
                  </Link>
                </div>
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  suffix={
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="pointer-events-auto text-surface-500 hover:text-surface-300 transition-colors"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                    </button>
                  }
                />
              </div>

              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-error-500/10 border border-error-500/20 text-error-400 text-sm"
                >
                  <AlertCircle className="size-4 shrink-0" />
                  {error}
                </motion.div>
              )}

              <Button
                type="submit"
                variant="primary"
                disabled={isLoading}
                className="w-full py-2.5"
              >
                {isLoading ? <Loader2 className="size-4 animate-spin" /> : "Sign In"}
              </Button>
            </form>

            <p className="text-center text-sm text-surface-400 mt-6">
              Don't have an account?{" "}
              <Link
                to="/sign-up"
                className="text-brand-400 hover:text-brand-300 font-medium transition-colors"
              >
                Create one
              </Link>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
